import {ISearchPanel, ISearchResultBuilder, ISvcSearch, JDefaultFields, JHit, JHitFields, JSearchResults, SearchPropComponent} from "./searchapi";
import {styleSheet} from "../commons/core";
import {JSX} from "wfr/commons/jsx";
import { Dict } from "wfr/commons/common";


export class SvcESSearch<T> implements ISvcSearch<T> {
	private cache: any = {}

	constructor(protected url:string) {}

	search(code: string, props?: any): Promise<JSearchResults<T>> {
		return new Promise((resolve, reject) => {
			const props_str = JSON.stringify(props||{})
			if (this.cache[code]?.[props_str]) resolve(this.cache[code][props_str])
			else {
				fetch(`${this.url}?${new URLSearchParams({"param": code}).toString()}`, {method:"POST", body: props_str})
					.then(resp => resp.json())
					.then(result => {
						const searchResults: JSearchResults<T> = {results: result.hits.hits, agg:{}}
						if (result.hits?.total?.relation === "eq") searchResults["size"] = result.hits.total.value
						if ("aggregations" in result) searchResults.agg = result.aggregations.global || result.aggregations
						if (!(code in this.cache)) this.cache[code] = {}
						this.cache[code][props_str] = searchResults
						resolve(searchResults)
					})
					.catch((error) => {
						reject(error)
					})
			}
		});

	}
}

export class SimpleEsResultBuilder implements ISearchResultBuilder{
	root:HTMLElement
	s = {
		moreResults : "￼Voir plus de résultats￼",
		noResults : "￼Aucun résultat￼",
		oneResult : "￼1 résultat￼",
		nResults : (size:number) => `￼${size} résultats￼`,
		nResultsOn : (size:number, total:number) => `￼${size} résultats sur ${total}￼`
	}
	constructor(protected urltree:string) {}

	resultsHook(results: JSearchResults<JHit>): JSearchResults<JHit> {return results}

	beforeResults(results: JSearchResults<JHit>, total:number): HTMLElement {
		const size = this.root.querySelectorAll("div.result").length + results.results.length
		if(total == 0) return <p class="size">{this.s.noResults}</p>
		else if(total == 1) return <p class="size">{this.s.oneResult}</p>
		else if(total == size) return <p class="size">{this.s.nResults(size)}</p>
		else return <p class="partial size">{this.s.nResultsOn(size, total)}</p>
	}

	afterResults(results: JSearchResults<JHit>, total:number): HTMLElement {
		const size = this.root.querySelectorAll("div.result").length
		if(size < total) {
			const button =  <button class="moreResult" title={this.s.moreResults}><span>{this.s.moreResults}</span></button>
			button.addEventListener("click", e=>{
				button.dispatchEvent(new CustomEvent("more-results", {detail:{size}, bubbles:true}))
				button.remove()
			})
			return button
		}
		return null;
	}

	beforeMoreResults(results: JSearchResults<JHit>, total:number){
		this.root.querySelector("p.size").outerHTML = this.beforeResults(results, total).outerHTML;
	}

	buildResult(result: JHitFields<JDefaultFields>): HTMLElement {
        return <div class="result {result.fields.processing[0]}">
				<a class="result" href={`${this.urltree}/${result.fields.path[0]}`}>
					<span>{result.fields.title?.[0]||result.fields.path[0].split("/").pop()}</span>
				</a>
		</div>
	}

	setRoot(element: HTMLElement): void {
		this.root = element
	}
}

export class SearchPanel extends HTMLElement implements ISearchPanel<JHit> {
	svc: ISvcSearch<JHit>
	searchRequest: string
	defaultSortKey:string
	resultBuilder: ISearchResultBuilder
	propComponents: SearchPropComponent[] = []
	total:number = 0

	template:string = `<div class="panel">
		<div class="head">
			<slot name="headPropsComponents"/>
		</div>
		<div class="body">
			<slot name="leftPropsComponents"/>
			<div class="content">
				<slot name="contentHeaderPropsComponents"/>
				<div class="results"/>
				<slot name="contentFooterPropsComponents"/>
			</div>
			<slot name="rightSideFilters"/>
		</div>
	</div>`

	async connectedCallback(): Promise<void> {
		this.searchRequest = this.getAttribute("request")
		this.defaultSortKey = this.getAttribute("defaultsort")
		this.attachShadow({mode: 'open'})
		this.shadowRoot.innerHTML = this.template
		this.shadowRoot!.adoptedStyleSheets.push(await styleSheet("./css/components.css"))

		this.querySelectorAll("[slot] *").forEach(element => {if(element instanceof SearchPropComponent) this.propComponents.push(element)});

		this.addEventListener("prop-change", event => this.search((event as CustomEvent).detail["search.force.props"]))
		this.addEventListener("more-results", event => this.more((event as CustomEvent).detail.from));
	}

	setSvc(svc: ISvcSearch<JHit>): void {
		this.svc = svc
	}

	setResultBuilder(resultBuilder: ISearchResultBuilder): void {
		this.resultBuilder = resultBuilder
		this.resultBuilder.setRoot(this.shadowRoot!.querySelector("div.results"))
	}


	async search(forceProps:Dict<any>) {
		let props:Dict<any> = {}
		if(forceProps["reset.search"]) {
			this.propComponents.forEach(propProvider => props = propProvider.mergeProps(props))
			delete forceProps["reset.search"]
		}
		for(const key in forceProps) {
			if(key == "delete.field") delete props[forceProps[key]]
			else props[key] = forceProps[key]
		}
		if(!props["sort"] && this.defaultSortKey) props["sort"] = this.defaultSortKey
		let results: JSearchResults<JHit> = await this.svc.search(this.searchRequest, props);
		this.propComponents.forEach(propProvider => propProvider.renderProps(props))
		results = this.resultBuilder.resultsHook(results);
		this.total = results.size || 0

		const resultsHtml = this.shadowRoot.querySelector("div.results")
		resultsHtml.innerHTML = ""
		resultsHtml.append(this.resultBuilder.beforeResults(results,this.total))
		resultsHtml.append(...results.results?.map(hit => this.resultBuilder.buildResult(hit)))
		resultsHtml.append(this.resultBuilder.afterResults(results, this.total))
	}

	async more(from:number) {
		let props:Dict<any> = {"from":from}
		this.propComponents.forEach(propProvider => props = propProvider.mergeProps(props))
		let results: JSearchResults<JHit> = await this.svc.search(`${this.searchRequest}More`, props);
		results = this.resultBuilder.resultsHook(results);

		const resultsHtml = this.shadowRoot.querySelector("div.results")
		this.resultBuilder.beforeMoreResults(results,this.total)
		resultsHtml.append(...results.results?.map(hit => this.resultBuilder.buildResult(hit)))
		resultsHtml.append(this.resultBuilder.afterResults(results, this.total))
	}
}

export class SearchTextInput extends SearchPropComponent{
  input:HTMLInputElement
	resetButton:HTMLButtonElement
	code:string
	reset:boolean
	placeHolder: string
	s = {
		resetTitle : "￼Réinitialiser la recherche￼",
		resetLabel : "￼Réinitialiser￼"
	}

	renderProps(props: any): void {
		this.input.value = props[this.code] || ""
	}

	mergeProps(props:any) {
		if(!props) props = {}
		if(this.code in props) delete props[this.code]
		if(this.input.value !== "") props[this.code] = this.input.value
		return props;
	}

	async connectedCallback(){
		this.code = this.hasAttribute("code") ? this.getAttribute("code") : "text";
		this.reset = this.hasAttribute("allow-reset-button")
		this.placeHolder = this.getAttribute("placeHolder");
		this.attachShadow({ mode: 'open' });
		this.shadowRoot!.adoptedStyleSheets.push(await styleSheet("./css/components.css"))
		this.shadowRoot.append(<input placeholder="${this.placeHolder||''}"></input>)
		if(this.reset) this.shadowRoot.append(<button class="reset" disabled="" title={this.s.resetTitle}><span>{this.s.resetLabel}</span></button>)

		this.input = this.shadowRoot.querySelector("input")
		this.resetButton = this.shadowRoot.querySelector("button")

		this.input.addEventListener("keyup", e=>{
			if(e.key === "Enter") {
				const detail:Dict<any> = {}
				detail[this.code] = this.input.value;
				this.dispatchEvent(new CustomEvent("prop-change", {detail:detail, bubbles:true}))
			}
			this.resetButton.disabled = this.input.value.length == 0
		});

		this.resetButton.addEventListener("click", e=>{
			this.input.value = "";
			this.resetButton.disabled = true
			const detail:Dict<any> = {}
			detail[this.code] = this.input.value;
			this.dispatchEvent(new CustomEvent("prop-change", {detail:detail, bubbles:true}))
		})
	}

}

export class SearchButton extends SearchPropComponent{
	label:string
	button:HTMLButtonElement

	async connectedCallback(){
		this.label = this.getAttribute("label")
		this.attachShadow({ mode: 'open' });
		this.shadowRoot!.adoptedStyleSheets.push(await styleSheet("./css/components.css"));
		this.shadowRoot.innerHTML = `<button title="${this.label}"><span>${this.label}</span></button>`;
		this.button = this.shadowRoot.querySelector("button")
		this.button.addEventListener("click", e=>this.dispatchEvent(new CustomEvent("prop-change", {detail:{}, bubbles:true})))
	}
}

export class SearchSortScore extends SearchPropComponent{
	label:string
	title:string
	s = {
		label:"￼Pertinence￼",
		title:"￼Trier par pertinence￼"
	}

	async connectedCallback(){
		this.label = this.hasAttribute("label") ? this.getAttribute("label") : this.s.label
		this.title = this.hasAttribute("title") ? this.getAttribute("title") : this.s.title
	}
	renderProps(props: Dict<any>) {
		this.shadowRoot.innerHTML = ""
		if(!props.text) return;

		if(!props.sort) this.shadowRoot.append(<span class="scoreSort sel"><span>{this.label}</span></span>)
		else{
			this.shadowRoot.append(<button title={this.title} class="scoreSort"><span>{this.label}</span></button>)
			const button = this.shadowRoot.querySelector("button")
			button.addEventListener("click", e=>this.dispatchEvent(new CustomEvent("prop-change", {detail:{"search.force.props":{"delete.field":"sort"}}, bubbles:true})))
		}
	}
}

export class SearchSortBidir extends SearchPropComponent{
	field:string
	label:string
	defaultDir:string
	reverseDir:string
	titleDir:string
	titleReverseDir:string

	async connectedCallback(){
		this.field = this.getAttribute("field")
		this.label = this.getAttribute("label")
		this.defaultDir = this.getAttribute("dirkey")
		this.reverseDir = this.getAttribute("rdirkey")
		this.titleDir = this.getAttribute("title")
		this.titleReverseDir = this.getAttribute("rtitle")
		this.attachShadow({ mode: 'open' });
		this.shadowRoot!.adoptedStyleSheets.push(await styleSheet("./css/components.css"));
	}

	mergeProps(props: Dict<any>): Dict<any> {
		const button = this.shadowRoot.querySelector("button")
		if(button) props.sort = button.getAttribute("data-key")
		return props;
	}

	renderProps(props: Dict<any>) {
		this.shadowRoot.innerHTML = ""
		if(props.sort == this.defaultDir) this.shadowRoot.append(<button title={this.titleReverseDir} class={`${this.defaultDir} sel`} data-key={this.reverseDir}><span>{this.label}</span></button>)
		else if(props.sort == this.reverseDir) this.shadowRoot.append(<button title={this.titleDir} class={`${this.reverseDir} sel`} data-key={this.defaultDir}><span>{this.label}</span></button>)
		else this.shadowRoot.append(<button title={this.titleDir} class={`${this.defaultDir}`} data-key={this.defaultDir}><span>{this.label}</span></button>)
		const button = this.shadowRoot.querySelector("button")
		button.addEventListener("click", e=>this.dispatchEvent(new CustomEvent("prop-change", {detail:{"search.force.props":{"sort":button.getAttribute("data-key")}}, bubbles:true})))
	}
}

customElements.define('search-panel', SearchPanel)
customElements.define('search-text-input', SearchTextInput)
customElements.define("search-button", SearchButton)
customElements.define("search-sort-bidir", SearchSortBidir)
customElements.define("search-sort-score", SearchSortScore)